import { useState } from 'react';
import type { ProposalPackageV1 } from '../../../worker/render/types';
import { AddButton, Field, ItemCard, NumberInput, SectionCard, TextInput, Toggle, WarningNote } from './fields';
import type { SectionProps } from './types';

const blankPackage: ProposalPackageV1 = {
  name: '',
  price: 0,
  shoot_duration: '',
  inclusions: [{ title: '' }],
};

export default function PackagesSection({ doc, onChange, disabled }: SectionProps) {
  const packages = doc.packages;
  const [active, setActive] = useState(0);
  const selected = Math.min(active, packages.length - 1);
  const duplicateNames = findDuplicates(packages.map((p) => p.name).filter(Boolean));

  function updatePackage(index: number, patch: Partial<ProposalPackageV1>) {
    onChange((d) => ({ ...d, packages: d.packages.map((p, i) => (i === index ? { ...p, ...patch } : p)) }));
  }

  function setRecommended(index: number, value: boolean) {
    onChange((d) => ({
      ...d,
      packages: d.packages.map((p, i) => ({ ...p, recommended: value ? i === index : i === index ? false : p.recommended })),
    }));
  }

  function addPackage() {
    if (packages.length >= 3) return;
    setActive(packages.length);
    onChange((d) => ({ ...d, packages: [...d.packages, { ...blankPackage, inclusions: [{ title: '' }] }] }));
  }

  function removePackage(index: number) {
    if (packages.length <= 1) return;
    onChange((d) => ({ ...d, packages: d.packages.filter((_, i) => i !== index) }));
  }

  return (
    <SectionCard title="Packages" hint="1 to 3 packages. Mark at most one as recommended.">
      {duplicateNames.length > 0 && <WarningNote>Two packages share the name "{duplicateNames[0]}". Give each a distinct name.</WarningNote>}

      <div className="package-tabs lg:hidden" role="tablist" aria-label="Packages">
        {packages.map((pkg, index) => <button type="button" role="tab" key={index} aria-selected={selected === index} onClick={() => setActive(index)}>{index + 1}. {pkg.name || 'New package'}</button>)}
      </div>
      <div className="space-y-5">
        {packages.map((pkg, index) => (
          <div key={index} className={selected === index ? "" : "hidden lg:block"}><PackageEditor
            pkg={pkg}
            disabled={disabled}
            onUpdate={(patch) => updatePackage(index, patch)}
            onSetRecommended={(value) => setRecommended(index, value)}
            onRemove={packages.length > 1 ? () => removePackage(index) : undefined}
          /></div>
        ))}
      </div>

      <AddButton onClick={addPackage} disabled={disabled || packages.length >= 3}>
        + Add package
      </AddButton>
    </SectionCard>
  );
}

function PackageEditor({
  pkg,
  disabled,
  onUpdate,
  onSetRecommended,
  onRemove,
}: {
  pkg: ProposalPackageV1;
  disabled?: boolean;
  onUpdate: (patch: Partial<ProposalPackageV1>) => void;
  onSetRecommended: (value: boolean) => void;
  onRemove?: () => void;
}) {
  const inclusions = pkg.inclusions;
  const addons = pkg.addons ?? [];
  const duplicateAddons = findDuplicates(addons.map((a) => a.title).filter(Boolean));

  function updateInclusion(index: number, patch: Partial<ProposalPackageV1['inclusions'][number]>) {
    onUpdate({ inclusions: inclusions.map((inc, i) => (i === index ? { ...inc, ...patch } : inc)) });
  }
  function addInclusion() {
    onUpdate({ inclusions: [...inclusions, { title: '' }] });
  }
  function removeInclusion(index: number) {
    if (inclusions.length <= 1) return;
    onUpdate({ inclusions: inclusions.filter((_, i) => i !== index) });
  }

  function updateAddon(index: number, patch: Partial<NonNullable<ProposalPackageV1['addons']>[number]>) {
    onUpdate({ addons: addons.map((a, i) => (i === index ? { ...a, ...patch } : a)) });
  }
  function addAddon() {
    onUpdate({ addons: [...addons, { title: '', price: 0 }] });
  }
  function removeAddon(index: number) {
    onUpdate({ addons: addons.filter((_, i) => i !== index) });
  }

  return (
    <div className="package-editor rounded-xl border border-line bg-paper p-5 space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name" hint="Outcome-derived, e.g. 'Be understood', not a size word.">
          <TextInput value={pkg.name} onChange={(e) => onUpdate({ name: e.target.value })} disabled={disabled} />
        </Field>
        <Field label="Price (whole AUD)">
          <NumberInput min={0} value={pkg.price} onChange={(value) => onUpdate({ price: value === '' ? 0 : value })} disabled={disabled} />
        </Field>
        <Field label="Shoot duration" hint="Optional, e.g. '2-3 Hours'.">
          <TextInput
            value={pkg.shoot_duration ?? ''}
            onChange={(e) => onUpdate({ shoot_duration: e.target.value })}
            disabled={disabled}
          />
        </Field>
        <div className="flex items-end">
          <Toggle checked={pkg.recommended === true} onChange={onSetRecommended} label="Recommended package" disabled={disabled} />
        </div>
      </div>

      <details open className="package-disclosure space-y-2.5">
        <summary className="text-sm font-medium text-ink">Inclusions <span className="font-mono">({inclusions.length})</span></summary>
        {inclusions.map((inclusion, index) => (
          <ItemCard key={index} onRemove={inclusions.length > 1 ? () => removeInclusion(index) : undefined} disabled={disabled}>
            <TextInput
              placeholder="Title"
              value={inclusion.title}
              onChange={(e) => updateInclusion(index, { title: e.target.value })}
              disabled={disabled}
            />
            <TextInput
              placeholder="Description (optional)"
              value={inclusion.description ?? ''}
              onChange={(e) => updateInclusion(index, { description: e.target.value })}
              disabled={disabled}
            />
          </ItemCard>
        ))}
        <AddButton onClick={addInclusion} disabled={disabled}>
          + Add inclusion
        </AddButton>
      </details>

      <BonusEditor pkg={pkg} disabled={disabled} onUpdate={onUpdate} />

      <details className="package-disclosure space-y-2.5">
        <summary className="text-sm font-medium text-ink">Optional add-ons <span className="font-mono">({addons.length})</span></summary>
        {duplicateAddons.length > 0 && <WarningNote>Two add-ons share the title "{duplicateAddons[0]}".</WarningNote>}
        {addons.map((addon, index) => (
          <ItemCard key={index} onRemove={() => removeAddon(index)} disabled={disabled}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr]">
              <TextInput
                placeholder="Title"
                value={addon.title}
                onChange={(e) => updateAddon(index, { title: e.target.value })}
                disabled={disabled}
              />
              <NumberInput
                min={0}
                placeholder="Price"
                value={addon.price}
                onChange={(value) => updateAddon(index, { price: value === '' ? 0 : value })}
                disabled={disabled}
              />
            </div>
            <TextInput
              placeholder="Description (optional)"
              value={addon.description ?? ''}
              onChange={(e) => updateAddon(index, { description: e.target.value })}
              disabled={disabled}
            />
          </ItemCard>
        ))}
        <AddButton onClick={addAddon} disabled={disabled}>
          + Add add-on
        </AddButton>
      </details>

      {onRemove && (
        <div className="flex justify-end border-t border-line pt-3">
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="text-xs font-medium text-ink-faint transition hover:text-error disabled:opacity-40"
          >
            Remove package
          </button>
        </div>
      )}
    </div>
  );
}

function BonusEditor({
  pkg,
  disabled,
  onUpdate,
}: {
  pkg: ProposalPackageV1;
  disabled?: boolean;
  onUpdate: (patch: Partial<ProposalPackageV1>) => void;
}) {
  const hasBonus = Boolean(pkg.bonus);
  const bonus = pkg.bonus;
  return (
    <div className="space-y-2.5">
      <Toggle
        checked={hasBonus}
        onChange={(checked) => onUpdate({ bonus: checked ? { title: '' } : undefined })}
        label="Include a bonus"
        disabled={disabled}
      />
      {hasBonus && bonus && (
        <ItemCard disabled={disabled}>
          <TextInput
            placeholder="Bonus title"
            value={bonus.title}
            onChange={(e) => onUpdate({ bonus: { ...bonus, title: e.target.value } })}
            disabled={disabled}
          />
          <TextInput
            placeholder="Description (optional)"
            value={bonus.description ?? ''}
            onChange={(e) => onUpdate({ bonus: { ...bonus, description: e.target.value } })}
            disabled={disabled}
          />
        </ItemCard>
      )}
    </div>
  );
}

function findDuplicates(values: string[]): string[] {
  const seen = new Set<string>();
  const dupes: string[] = [];
  for (const value of values) {
    if (seen.has(value)) dupes.push(value);
    seen.add(value);
  }
  return dupes;
}
